<template>
  <div class="service-tile" :class="`service-tile--${tile.state}`" @click="$emit('open', tile.card.id)">
    <div class="service-tile__cover">
      <v-img v-if="tile.card.main_image_url" :src="tile.card.main_image_url" height="110" contain />
      <v-icon v-else size="56" color="grey lighten-1">mdi-clipboard-text-outline</v-icon>
      <div v-if="tile.state === 'company_off'" class="service-tile__overlay">
        <span class="service-tile__badge">Не куплена компанией</span>
      </div>
    </div>

    <div class="service-tile__body">
      <div class="service-tile__info">
        <div class="service-tile__badge" :class="`service-tile__badge--${tile.state}`">{{ stateTitle }}</div>
        <div class="service-tile__name">{{ tile.card.name }}</div>
        <div class="service-tile__desc">{{ tile.card.short_description }}</div>

        <div v-if="tile.balance !== null" class="service-tile__balance">
          <span class="service-tile__amount">{{ tile.balance }} шт.</span>
          <span v-if="tile.bufferBalance !== null" class="caption grey--text text--darken-1">в буфере {{ tile.bufferBalance }}</span>
          <span v-else-if="tile.state === 'available'" class="caption grey--text text--darken-1">ваш остаток</span>
        </div>
      </div>

      <div class="service-tile__actions" @click.stop>
        <template v-if="tile.state === 'in_cart'">
          <BasketCounter :id="tile.card.id" />
        </template>
        <span v-if="tile.state === 'ask'" class="caption grey--text text--darken-1">Запросите у руководителя</span>
        <v-btn v-if="tile.state === 'pending' && canBuy" small outlined color="primary" :to="{ name: 'my-orders' }">Мои заказы</v-btn>
        <span v-else-if="tile.state === 'pending'" class="caption grey--text text--darken-1">Компания оформила заказ</span>
        <!-- В корзину кладётся одна штука, дальше карточка показывает счётчик (стрелки или ввод числа) -->
        <v-btn
          v-if="showBuy" small :color="tile.state === 'ended' ? 'warning' : 'primary'"
          :outlined="tile.state !== 'ended' && tile.state !== 'not_bought'" :loading="busy"
          @click="$emit('add-to-cart', tile.card.id)"
        >
          <v-icon left small>mdi-cart-plus</v-icon> {{ buyLabel }}
        </v-btn>
        <v-btn small text color="primary" @click="$emit('open', tile.card.id)">Подробнее</v-btn>
      </div>
    </div>
  </div>
</template>

<script>
import BasketCounter from '@/components/basket/BasketCounter.vue';

// Состояния, где услугу можно положить в корзину (not_bought показывается без проверки права покупки)
const BUY_STATES = ['available', 'ended', 'company_off', 'not_bought', 'catalog'];

const STATE_TITLES = {
  available: 'Доступна',
  ended: 'Закончилась',
  pending: 'Ожидает оплаты',
  not_bought: 'Не куплена',
  in_cart: 'В корзине',
  ask: 'Закончилась',
  company_off: 'Не куплена',
  catalog: 'В каталоге',
};

// Карточка каталога (ТЗ, раздел 6): широкая строка на всю ширину, на узком экране — столбиком.
// Состояние считает родитель (listServices), карточка только показывает.
export default {
  name: 'ServiceTile',
  components: { BasketCounter },
  props: {
    tile: { type: Object, required: true },
    canBuy: { type: Boolean, default: false },
    busy: { type: Boolean, default: false },
  },
  computed: {
    showBuy() {
      return BUY_STATES.includes(this.tile.state) && (this.canBuy || this.tile.state === 'not_bought');
    },
    buyLabel() {
      return this.tile.state === 'ended' || this.tile.state === 'available' ? 'Докупить' : 'В корзину';
    },
    stateTitle() {
      if (this.tile.state === 'available' && !this.tile.isTest) return 'Куплена';
      return STATE_TITLES[this.tile.state] || '';
    },
  },
};
</script>

<style scoped lang="scss">
.service-tile {
  display: flex;
  flex-direction: column;
  background: #fff;
  border: 1px solid #e0e0e0;
  border-radius: 10px;
  overflow: hidden;
  cursor: pointer;
  transition: box-shadow .15s;

  @media (min-width: 600px) {
    flex-direction: row;
  }

  &:hover {
    box-shadow: 0 2px 10px rgba(0, 0, 0, .08);
  }

  &--ended {
    border: 2px solid #f9a825;
  }

  &--in_cart {
    border: 2px solid var(--v-primary-base, #1976d2);
  }

  &__cover {
    position: relative;
    display: flex;
    align-items: center;
    justify-content: center;
    height: 120px;
    background: #f7f8fa;

    @media (min-width: 600px) {
      flex: 0 0 200px;
      height: auto;
      min-height: 130px;
    }
  }

  &--not_bought &__cover > .v-image,
  &--not_bought &__cover > .v-icon,
  &--company_off &__cover > .v-image,
  &--company_off &__cover > .v-icon {
    opacity: .6;
  }

  &__overlay {
    position: absolute;
    inset: 0;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  &__body {
    display: flex;
    flex-direction: column;
    flex: 1;
    padding: 12px 14px;
    gap: 4px;

    @media (min-width: 600px) {
      flex-direction: row;
      align-items: center;
      gap: 24px;
      padding: 16px 20px;
    }
  }

  &__info {
    display: flex;
    flex-direction: column;
    flex: 1 1 auto;
    min-width: 0;
    gap: 4px;
  }

  &__badge {
    align-self: flex-start;
    padding: 1px 8px;
    border-radius: 10px;
    font-size: 11px;
    background: #eceff1;
    color: #546e7a;

    &--available { background: #e8f5e9; color: #2e7d32; }
    &--ended { background: #fff8e1; color: #f57f17; }
    &--pending { background: #fff3e0; color: #e65100; }
    &--in_cart { background: #e3f2fd; color: #1565c0; }
  }

  &__name {
    font-weight: 600;
    font-size: 15px;
    color: #212121;
  }

  &__desc {
    font-size: 13px;
    color: #757575;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
  }

  &__balance {
    display: flex;
    align-items: baseline;
    gap: 8px;
    margin-top: 4px;
  }

  &__amount {
    font-size: 20px;
    font-weight: 600;
  }

  &--not_bought &__balance,
  &--company_off &__balance {
    filter: blur(3px);
  }

  &__actions {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 6px;
    margin-top: auto;
    padding-top: 8px;

    @media (min-width: 600px) {
      flex: 0 0 auto;
      justify-content: flex-end;
      margin-top: 0;
      padding-top: 0;
    }
  }
}
</style>
